import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { Card, Box, Typography, Snackbar, Alert, Divider, Tabs, Tab, Button, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  getAllApplicationNotes,
  getApplication,
  handleGoBackAppDetailsSummary,
  handleTabChangeAppDetailsSummary,
  selectApplicationsState,
} from '../../../../resui/applications/sagas/pm/applicationsSlice';
import { Redirect, Route, Switch, useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import ExternalRefPropertyIcon from '../../../../icons/ExternalRefPropertyIcon.svg';
import MaintenanceBackIcon from '../../../../icons/MaintenanceBackIcon.svg';
import ApplicationDetailsSummary from './applicationDetailsSummary/ApplicationDetailsSummary';
import { ApplicationOccupantSummaryDto, ApplicationOccupantSummaryDtoStatusEnum, ApplicationSummaryDto } from '@inosio/payments-sdk';
import SpinnerWithNoConfirmation from '../../../../shared/Modal/spinnerWithNoConfirmation';
import OrderScreeningOptionsModal from '../OrderScreeningOptionsModal';
import ScreeningSpyBlueIcon from '../../../../icons/ScreeningSpyBlue.svg';
import DecisionApplicationModal from '../DecisionApplicationModal';
import ApplicationDetailsApplicant from './ApplicationDetailsApplicant';
import ApplicationDetailsGeneral from './ApplicationDetailsGeneral';
import ApplicationDetailsMinors from './ApplicationDetailsMinors';
import ApplicationDetailsAnimals from './ApplicationDetailsAnimals';
import ApplicationDetailsVehicles from './ApplicationDetailsVehicles';
import UpdateApplicationPropertyModal from './UpdateApplicationPropertyModal';
import EditPinIcon from '../../../../icons/EditPinIcon.svg';
import ReturnApplicationModal from '../ReturnApplicationModal';

const useStyles = makeStyles({
  gridContainer: {
    background: '#FFFFFF',
    borderRadius: '24px',
  },
  decisionButton: {
    borderRadius: '24px',
    textTransform: 'capitalize',
    color: 'white',
    backgroundColor: '#39A2DB',
    borderColor: '#39A2DB',
    fontFamily: 'Work Sans !important',
  },
});

const styles = {
  tabSx: {
    textTransform: 'capitalize',
    '&.Mui-selected': {
      color: '#000 !important',
      border: 'none !important',
    },
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: '600 !important',
    fontSize: '18px !important',
    lineHeight: '22px !important',
    color: '#A8A8A8 !important',
    margin: '0px 5px !important',
    maxWidth: '200px !important',
  },
  decisionButton: {
    borderRadius: '24px',
    fontFamily: 'Work Sans !important',
    textTransform: 'capitalize',
    color: 'white',
    backgroundColor: '#39A2DB',
    borderColor: '#39A2DB',
  },
  orderScreeningButton: {
    borderRadius: '24px',
    fontFamily: 'Work Sans !important',
    textTransform: 'capitalize',
    color: '#39A2DB',
    borderColor: '#39A2DB',
  },
  tabsContainer: {
    '& .MuiTabs-flexContainer': {
      width: '100%',
      maxWidth: '100%',
      display: 'flex',
      justifyContent: 'space-between',
    },
    '& .MuiTabs-indicator': {
      display: 'none !important',
    },
  },
  wizardCard: {
    mt: 2,
    p: 2,
    height: 'calc( 100vh - 144px )',
  },
  spinnerContainer: { width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center', height: 'calc( 100vh - 294px )' },
  navigationHeaderContainer: { display: 'flex', p: 1, pb: 2, justifyContent: 'space-between', alignItems: 'center', '& .MuiTextField-root': { m: 1, width: '25ch' } },
};

//the order here matters
export enum ApplicationDetailsWizardPageStates {
  Summary = 'summary',
  Applicant = 'applicant',
  General = 'general',
  Animals = 'animals',
  Minors = 'minors',
  Vehicles = 'vehicles',
}

export default function ApplicationDetailsWizard() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  //selectors
  const { pathname } = useLocation();
  let { url } = useRouteMatch();
  const { state: applicationDetailsWizardState } = useLocation<{ returnQueryString: string }>();
  const { id: applicationUuidParam } = useParams<{ id: string }>();
  const { application, isGettingApplicationFailed, isGettingApplicationSuccess, isUpdatingAppPropertySuccess } = useSelector(selectApplicationsState);
  const { isChangingApplicationStatusSuccess, changeApplicationStatusResponse, isSendingApplicationDecisionNotificationSuccess } = useSelector(selectApplicationsState);

  //local state
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [showSpinner, setShowSpinner] = useState<boolean>(true);
  const [openOrderScreeningOptionsModal, setOpenOrderScreeningOptionsModal] = useState(false);
  const [appSummaryObj, setAppSummaryObj] = useState<ApplicationSummaryDto | undefined>(undefined);
  const [openAppStatusModal, setOpenAppStatusModal] = useState(false);
  const [openUpdateAppPropertyModal, setOpenUpdateAppPropertyModal] = useState(false);
  const [openAppReturnModal, setOpenAppReturnModal] = useState(false);
  const [returnAppSuccessSnackBarOpen, setReturnAppSuccessSnackBarOpen] = useState(false);

  /**
   * Computes the current tab the user is on
   * @returns
   */
  const computeCurrentTab = () => {
    /**
     * Algorithm:
     * Check if the last part of the path is a valid tab (a WizardPageState). If it is, return the tab. In this case, show the tab carousel.
     * If the last part of the path is not a valid tab but instead a parameter, check the second-to-last part of the path. This should give you a valid tab.
     * If that doesnt give a valid tab then just return the default tab.
     */
    const parts = pathname.split('/').filter(Boolean); // Split pathname into parts and remove empty parts
    const lastPartOfPath = parts[parts.length - 1]; // Get the last part of the path
    const secondLastPartOfPath = parts[parts.length - 2]; // Get the second to last part of the path

    if (Object.values(ApplicationDetailsWizardPageStates).includes(lastPartOfPath as ApplicationDetailsWizardPageStates)) {
      // It's a valid tab
      return lastPartOfPath as ApplicationDetailsWizardPageStates;
    } else if (parts.length > 1 && Object.values(ApplicationDetailsWizardPageStates).includes(secondLastPartOfPath as ApplicationDetailsWizardPageStates)) {
      // Check if the second-to-last part is a valid tab
      return parts[parts.length - 2] + '/' + lastPartOfPath;
    } else {
      // Default tab
      return ApplicationDetailsWizardPageStates.Summary;
    }
  };

  const myCurrenTab = computeCurrentTab();

  // we have to tell material ui what the valid tabs are gonna be, otherwise it will yell at you
  const validTabs = [
    ApplicationDetailsWizardPageStates.Summary,
    ApplicationDetailsWizardPageStates.General,
    ApplicationDetailsWizardPageStates.Minors,
    ApplicationDetailsWizardPageStates.Animals,
    ApplicationDetailsWizardPageStates.Vehicles,
    `${ApplicationDetailsWizardPageStates.Applicant}/${application.PrimaryApplicant?.Uuid}`,
  ].concat(application.CoApplicants?.map((coapp) => `${ApplicationDetailsWizardPageStates.Applicant}/${coapp?.Uuid}`) || []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: ApplicationDetailsWizardPageStates) => {
    //refreshing the states bcz some states are causing unneccesary sideffects when navigating back and forth
    dispatch(handleTabChangeAppDetailsSummary());
    history.push(`/pmui/applications/${applicationUuidParam}/${newValue.toLowerCase()}`, { returnQueryString: applicationDetailsWizardState?.returnQueryString || '' });
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackBarOpen(false);
  };

  const handleReturnAppSuccessSnackBarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setReturnAppSuccessSnackBarOpen(false);
  };

  //on load fetch the application details
  useEffect(() => {
    dispatch({
      type: getApplication.type,
      payload: {
        applicationUuid: applicationUuidParam,
      },
    });
  }, [dispatch, applicationUuidParam]);

  //on load fetch the application details
  useEffect(() => {
    if (isUpdatingAppPropertySuccess) {
      dispatch({
        type: getApplication.type,
        payload: {
          applicationUuid: applicationUuidParam,
        },
      });
    }
  }, [dispatch, isUpdatingAppPropertySuccess]);

  //on load fetch all application notes
  useEffect(() => {
    dispatch({
      type: getAllApplicationNotes.type,
      payload: {
        applicationUuid: applicationUuidParam,
      },
    });
  }, [dispatch]);

  //once the details fetch successful prepare the params for the details summary component
  useEffect(() => {
    if (isGettingApplicationSuccess) {
      const allApplicantsOnThisApplication = [...(application.CoApplicants || []), application.PrimaryApplicant];
      setAppSummaryObj({
        Uuid: application.Uuid,
        Status: application.Status,
        PropertyAddress: application.PropertyAddress,
        OrganizationUuid: application.OrganizationUuid,
        OrganizationName: application.OrganizationName,
        CreatedDate: application.CreatedDate,
        PrimaryApplicantUuid: application.PrimaryApplicant.Uuid,
        Applicants: allApplicantsOnThisApplication.map((applicant) => {
          return {
            Uuid: applicant.Uuid,
            Email: applicant.Email,
            UserRegistrationDate: applicant.UserRegistrationDate,
            LastName: applicant.LastName,
            MiddleName: applicant.MiddleName,
            FirstName: applicant.FirstName,
            Status: applicant.Status as unknown as ApplicationOccupantSummaryDtoStatusEnum,
          } as ApplicationOccupantSummaryDto;
        }),
        ScreeningOrders: application.ScreeningOrders,
      });
      setGlobalError('');
      setShowSpinner(false);
      setSnackBarOpen(false);
    } else if (isGettingApplicationFailed) {
      setSnackBarOpen(true);
      setShowSpinner(false);
      setGlobalError(`Apologies, the request to view application details failed. Please try again later or contact support. Thank you.`);
    }
  }, [isGettingApplicationFailed, isGettingApplicationSuccess]);

  //success handling for returning application, failure is handled in the return app modal
  useEffect(() => {
    if (isSendingApplicationDecisionNotificationSuccess && isChangingApplicationStatusSuccess && changeApplicationStatusResponse?.ApplicationStatus === 'RETURNED') {
      setReturnAppSuccessSnackBarOpen(true);
      //refetch the app so the status is updated
      dispatch({
        type: getApplication.type,
        payload: {
          applicationUuid: applicationUuidParam,
        },
      });
    }
  }, [isSendingApplicationDecisionNotificationSuccess, isChangingApplicationStatusSuccess, changeApplicationStatusResponse]);

  return (
    <Box>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={snackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {globalError}
        </Alert>
      </Snackbar>
      <Card sx={styles.wizardCard} className={classes.gridContainer}>
        <Box sx={styles.navigationHeaderContainer}>
          <Box display="flex" alignItems="center">
            <img
              src={MaintenanceBackIcon}
              style={{ height: '20px', width: '20px', marginRight: '30px', cursor: 'pointer' }}
              alt="back"
              onClick={() => {
                //to avoid unneccessary sideffects refresh the states when you leave the page
                dispatch(handleGoBackAppDetailsSummary());
                history.push(`/pmui/applications${applicationDetailsWizardState?.returnQueryString || ''}`);
              }}
            />
            <img src={ExternalRefPropertyIcon} style={{ height: '20px', width: '20px' }} alt="ExternalRefPropertyIcon" />
            <Typography id="subTitle" ml={2}>{`${application?.PropertyAddress || ''}`}</Typography>
            <img
              src={EditPinIcon}
              alt="Edit property"
              style={{ cursor: 'pointer', width: 13, height: 17, marginLeft: '10px' }}
              onClick={() => {
                setOpenUpdateAppPropertyModal(true);
              }}
            />
          </Box>
          {!showSpinner && isGettingApplicationSuccess && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {application.Status !== 'INCOMPLETE' && (
                <Button
                  variant="outlined"
                  sx={styles.orderScreeningButton}
                  onClick={() => {
                    setOpenAppReturnModal(true);
                  }}
                >
                  Return to Applicant
                </Button>
              )}
              {application.Status === 'SUBMITTED' && (
                <Button
                  startIcon={<img src={ScreeningSpyBlueIcon} alt="order screening" />}
                  variant="outlined"
                  sx={styles.orderScreeningButton}
                  onClick={() => {
                    setOpenOrderScreeningOptionsModal(true);
                  }}
                >
                  Order Screening
                </Button>
              )}
              {application.Status === 'SUBMITTED' && (
                <Button
                  variant="contained"
                  sx={styles.decisionButton}
                  onClick={() => {
                    setOpenAppStatusModal(true);
                  }}
                >
                  Decision Application
                </Button>
              )}
            </Stack>
          )}
        </Box>
        <Divider />
        <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
          <Tabs
            value={validTabs.includes(myCurrenTab) ? myCurrenTab : ApplicationDetailsWizardPageStates.Summary}
            sx={styles.tabsContainer}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="scrollable auto tabs"
          >
            <Tab sx={styles.tabSx} label="Summary" value={ApplicationDetailsWizardPageStates.Summary} />
            <Tab sx={styles.tabSx} label="General" value={ApplicationDetailsWizardPageStates.General} />
            <Tab sx={styles.tabSx} label="Applicant 1" value={`${ApplicationDetailsWizardPageStates.Applicant}/${application.PrimaryApplicant?.Uuid}`} />
            {application.CoApplicants?.length &&
              application.CoApplicants?.map((coapp, idx) => {
                return <Tab key={coapp.Uuid} sx={styles.tabSx} label={`Applicant ${idx + 2}`} value={`${ApplicationDetailsWizardPageStates.Applicant}/${coapp?.Uuid}`} />;
              })}
            <Tab sx={styles.tabSx} label="Minor Occupants" value={ApplicationDetailsWizardPageStates.Minors} />
            <Tab sx={styles.tabSx} label="Animals" value={ApplicationDetailsWizardPageStates.Animals} />
            <Tab sx={styles.tabSx} label="Vehicles" value={ApplicationDetailsWizardPageStates.Vehicles} />
          </Tabs>
        </Box>
        <Divider />
        {showSpinner ? (
          <Box sx={styles.spinnerContainer}>
            <SpinnerWithNoConfirmation />
          </Box>
        ) : (
          isGettingApplicationSuccess &&
          application && (
            <Switch>
              <Route exact path={url}>
                <Redirect to={`${url}/${ApplicationDetailsWizardPageStates.Summary.toLowerCase()}`} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.Summary.toLowerCase()}`}>
                <ApplicationDetailsSummary application={application} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.General.toLowerCase()}`}>
                <ApplicationDetailsGeneral application={application} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.Applicant.toLowerCase()}/:id`}>
                <ApplicationDetailsApplicant application={application} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.Minors.toLowerCase()}`}>
                <ApplicationDetailsMinors application={application} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.Animals.toLowerCase()}`}>
                <ApplicationDetailsAnimals application={application} />
              </Route>
              <Route path={`${url}/${ApplicationDetailsWizardPageStates.Vehicles.toLowerCase()}`}>
                <ApplicationDetailsVehicles application={application} />
              </Route>
              <Redirect from="*" to={`${url}/${ApplicationDetailsWizardPageStates.Summary.toLowerCase()}`} />
            </Switch>
          )
        )}
      </Card>
      {openOrderScreeningOptionsModal && (
        <OrderScreeningOptionsModal
          open={openOrderScreeningOptionsModal}
          onClose={() => {
            setOpenOrderScreeningOptionsModal(false);
          }}
          application={appSummaryObj || ({} as ApplicationSummaryDto)}
        />
      )}

      <DecisionApplicationModal open={openAppStatusModal} propertyGroupUuid={appSummaryObj?.OrganizationUuid || ''} onClose={() => setOpenAppStatusModal(false)} application={appSummaryObj} />

      <UpdateApplicationPropertyModal
        open={openUpdateAppPropertyModal}
        onClose={() => {
          setOpenUpdateAppPropertyModal(false);
        }}
        oldPropertyName={application?.PropertyAddress || ''}
        appUuid={applicationUuidParam}
      />
      {openAppReturnModal && (
        <ReturnApplicationModal
          open={openAppReturnModal}
          onClose={() => {
            setOpenAppReturnModal(false);
          }}
          application={appSummaryObj}
        />
      )}
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={returnAppSuccessSnackBarOpen} autoHideDuration={4000} onClose={handleReturnAppSuccessSnackBarClose}>
        <Alert onClose={handleReturnAppSuccessSnackBarClose} severity="success">
          Application successfully returned to the applicant for updates.
        </Alert>
      </Snackbar>
    </Box>
  );
}
